// @ts-nocheck
/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import useAppStore from '../store/index';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { evaluateRentabilitaet } from '../store/utils';
import UI from '../components/UI';
import EditableField from '../components/EditableField';
import { db, auth, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, onSnapshot, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import useAdminStore from '../store/adminStore';
import { Upload, Loader2, FileCheck, FileText, Trash2, X, Download, AlertTriangle, Calculator, User, Plus as PlusIcon } from 'lucide-react';

const IconList = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
        <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z"></path>
    </svg>
);

const IconTrash = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} fill="currentColor" viewBox="0 0 256 256">
        <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
    </svg>
);

const formatNumberOnly = (val: number) => new Intl.NumberFormat('de-DE', { 
    style: 'decimal', 
    maximumFractionDigits: 0, 
    minimumFractionDigits: 0 
}).format(val || 0);

const formatCurrency = (val: number, currency: string = 'EUR') => new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: currency,
    maximumFractionDigits: 2, 
    minimumFractionDigits: 2 
}).format(val || 0);

const BWA_PLAN_ZIEL_HINTS: Record<string, string> = {
    raumkosten: '10-12% Planziel',
    werbeReisekosten: '2-5% Planziel',
    sonstigeKosten: 'ca. 10-12% Planziel',
};

const BWA_PLAN_ZIEL_HINT_CLASS = 'text-xs text-orange-700 dark:text-orange-500 ml-2 whitespace-nowrap';

/** Perioden-Totals, die von N Monaten auf 12 hochskaliert werden dürfen */
const BWA_SCALE_KEYS = [
    'umsatz',
    'material',
    'raumkosten',
    'betriebssteuern',
    'versicherungen',
    'besondereKosten',
    'kfzKosten',
    'werbeReisekosten',
    'warenabgabe',
    'reparatur',
    'sonstigeKosten',
    'kredite',
    'zinsaufwendungen',
    'gewerbesteuer',
    'koerperschaftssteuer',
    'budgetPlansumme',
    'sonstigeEintrag1',
    'sonstigeEintrag2',
    'sonstigeEintrag3',
    'sonstigeEintrag4',
    'abschreibungen',
    'zinsenMiete',
    'geraetEintrag1',
    'geraetEintrag2',
    'geraetEintrag3',
    'geraetEintrag4',
    'geraetEintrag5',
    'geraetEintrag6',
    'geraetEintrag7',
    'geraetEintrag8',
    'geraetEintrag9',
    'geraetEintrag10',
    'premiumSoftware',
] as const;

const formatIntCurrency = (val: number, currency: string = 'EUR') => new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: currency,
    maximumFractionDigits: 0, 
    minimumFractionDigits: 0 
}).format(val || 0);


const CostTool = ({ calc }: { calc: any }) => {
    const currency = calc.currencyCode || 'EUR';
    
    const MOCK_COSTS = useMemo(() => ({
        monthlyTotal: calc.monatsumsatzNetto || 23750,  
        monthsPerYear: calc.zeitraum || 12,    
        vatRate: calc.mwstSatz !== undefined && calc.mwstSatz !== null ? Number(calc.mwstSatz) : 19.0        
    }), [calc.monatsumsatzNetto, calc.zeitraum, calc.mwstSatz]);

    const [percInput, setPercInput] = useState(12.0);
    const [timeMode, setTimeMode] = useState('monthToYear');
    const [timeVal, setTimeVal] = useState(300); 
    const [taxMode, setTaxMode] = useState('nettoToBrutto');
    const [taxVal, setTaxVal] = useState(2000); 

    const percMonthly = useMemo(() => MOCK_COSTS.monthlyTotal * (percInput / 100), [percInput, MOCK_COSTS.monthlyTotal]);
    const percYearly = useMemo(() => percMonthly * MOCK_COSTS.monthsPerYear, [percMonthly, MOCK_COSTS.monthsPerYear]);

    const timeResult = useMemo(() => {
        if (timeMode === 'monthToYear') return timeVal * MOCK_COSTS.monthsPerYear;
        return timeVal / MOCK_COSTS.monthsPerYear;
    }, [timeVal, timeMode, MOCK_COSTS.monthsPerYear]);

    const taxResult = useMemo(() => {
        if (taxMode === 'nettoToBrutto') return taxVal * (1 + MOCK_COSTS.vatRate / 100);
        return taxVal / (1 + MOCK_COSTS.vatRate / 100);
    }, [taxVal, taxMode, MOCK_COSTS.vatRate]);

    const handleTimeToggle = (mode: string) => setTimeMode(mode);
    const handleTaxToggle = (mode: string) => setTaxMode(mode);

    const [entries, setEntries] = useState<number[]>([]);
    const [inputVal, setInputVal] = useState<number | ''>(''); 
    const [convRes, setConvRes] = useState<number | null>(null);
    const listSum = useMemo(() => entries.reduce((a, b) => a + b, 0), [entries]);

    const handleListAdd = (factor: number) => {
        const val = typeof inputVal === 'number' ? inputVal : 0;
        if (val !== 0) {
            setEntries([...entries, val * factor]);
            setInputVal(''); 
            setConvRes(null);
        }
    };

    const handleListRemove = (index: number) => {
        const newEntries = [...entries];
        newEntries.splice(index, 1);
        setEntries(newEntries);
        setConvRes(null);
    };

    const handleListClear = () => {
        setEntries([]);
        setConvRes(null);
        setInputVal('');
    };

    const handleListConvert = (factor: number) => {
        if (listSum === 0) return;
        setConvRes(listSum * factor);
    };

    const resultLabelClass = "text-[10px] text-pe-accent-text dark:text-[#E6C299] uppercase mb-1 font-bold tracking-wide";
    const inputLabelClass = "text-[10px] text-pe-muted dark:text-slate-400 uppercase mb-1 text-center block tracking-wide font-semibold";
    const rowGridClass = "grid grid-cols-[180px_160px_1fr_1fr] gap-6 items-center border-b border-pe-border dark:border-slate-800 pb-6";
    const rowGridClassToggle = "grid grid-cols-[180px_160px_auto_1fr] gap-6 items-center border-b border-pe-border dark:border-slate-800 pb-6";
    const toggleBtnClass = (isActive: boolean) => `flex items-center justify-center px-4 py-2 text-xs font-bold uppercase rounded transition-all whitespace-nowrap ${isActive ? 'bg-stone-700 dark:bg-[#1e293b] text-white shadow-sm' : 'text-pe-muted dark:text-slate-400 hover:bg-pe-hover dark:hover:bg-slate-800'}`;

    return (
        <div className="bg-pe-elevated dark:bg-[#0f1015] rounded-xl border border-pe-border dark:border-slate-800 shadow-xl overflow-hidden flex flex-col font-sans text-pe-text dark:text-white mt-8 text-left">
            <div className="bg-pe-card dark:bg-[#15171e] p-4 border-b border-pe-border dark:border-slate-800 flex justify-between items-center text-left">
                <div className="flex items-center gap-2">
                    <IconList />
                    <h3 className="font-bold text-pe-text dark:text-white text-lg">Berechnungstool (Scratchpad)</h3>
                </div>
            </div>

            <div className="p-6 flex flex-col gap-6 text-left">
                <div className={rowGridClass}>
                    <span className="text-base text-pe-accent-text dark:text-[#E6C299] font-bold">Prozent Rechner</span>
                    <div>
                        <span className={inputLabelClass}>EINGABE (%)</span>
                        <EditableField value={percInput} onChange={setPercInput} type="percentage" className="text-lg bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 h-10" />
                    </div>
                    <div>
                        <span className={resultLabelClass}>ERGEBNIS / MONAT</span>
                        <EditableField value={percMonthly} readOnly type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="bg-transparent border-transparent" />
                    </div>
                    <div>
                        <span className={resultLabelClass}>ERGEBNIS / JAHR</span>
                        <EditableField value={percYearly} readOnly type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="bg-transparent border-transparent" />
                    </div>
                </div>

                <div className={rowGridClassToggle}>
                    <span className="text-base text-pe-accent-text dark:text-[#E6C299] font-bold">Zeitraum</span>
                    <div>
                        <span className={inputLabelClass}>EINGABE ({currency})</span>
                        <EditableField value={timeVal} onChange={setTimeVal} type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="text-lg bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 h-10" />
                    </div>
                    <div className="flex flex-col justify-end h-full">
                        <span className={inputLabelClass}>MODUS WÄHLEN</span>
                        <div className="flex bg-pe-tile-header dark:bg-[#08090b] rounded p-1 border border-pe-border-strong dark:border-slate-700 h-10 gap-1">
                            <button onClick={() => handleTimeToggle('monthToYear')} className={toggleBtnClass(timeMode === 'monthToYear')}>Monat ➔ Jahr</button>
                            <button onClick={() => handleTimeToggle('yearToMonth')} className={toggleBtnClass(timeMode === 'yearToMonth')}>Jahr ➔ Monat</button>
                        </div>
                    </div>
                    <div>
                         <span className={resultLabelClass}>ERGEBNIS</span>
                         <EditableField value={timeResult} readOnly type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="bg-transparent border-transparent" />
                    </div>
                </div>

                <div className={rowGridClassToggle}>
                    <span className="text-base text-pe-accent-text dark:text-[#E6C299] font-bold">MwSt. Rechner</span>
                    <div>
                        <span className={inputLabelClass}>EINGABE ({currency})</span>
                        <EditableField value={taxVal} onChange={setTaxVal} type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="text-lg bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 h-10" />
                    </div>
                    <div className="flex flex-col justify-end h-full">
                        <span className={inputLabelClass}>RICHTUNG</span>
                        <div className="flex bg-pe-tile-header dark:bg-[#08090b] rounded p-1 border border-pe-border-strong dark:border-slate-700 h-10 gap-1">
                            <button onClick={() => handleTaxToggle('nettoToBrutto')} className={toggleBtnClass(taxMode === 'nettoToBrutto')}>Netto ➔ Brutto</button>
                            <button onClick={() => handleTaxToggle('bruttoToNetto')} className={toggleBtnClass(taxMode === 'bruttoToNetto')}>Brutto ➔ Netto</button>
                        </div>
                    </div>
                    <div>
                        <span className={resultLabelClass}>ERGEBNIS ({MOCK_COSTS.vatRate}%)</span>
                        <EditableField value={taxResult} readOnly type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} className="bg-transparent border-transparent" />
                    </div>
                </div>

                <div className="flex flex-col gap-4 pt-2">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4 text-left">
                            <span className="text-sm text-pe-accent-text dark:text-[#E6C299] font-bold uppercase tracking-widest">Additions-Liste</span>
                            {entries.length > 0 && (
                                <button onClick={handleListClear} className="text-[10px] bg-red-100 dark:bg-red-900/50 hover:bg-red-200 dark:hover:bg-red-900 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-800 rounded px-2 py-1 uppercase font-bold transition-colors">
                                    Alles löschen
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-[1fr_110px_110px] gap-2 items-end">
                        <div onKeyDown={(e: any) => { if (e.key === 'Enter') handleListAdd(1); }}>
                            <span className={inputLabelClass}>EINGABE ZAHL ({currency})</span>
                            <EditableField value={inputVal} onChange={(val: number | '') => setInputVal(val)} type="currency" currency={currency} decimalScale={0} decimalsLimit={0} decimalPlaces={0} precision={0} placeholder={`0 ${currency}`} className="h-[44px] text-lg bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700" />
                        </div>
                        <button onClick={() => handleListAdd(1)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold border border-emerald-500 rounded transition-all h-[44px]">
                            + Dazu
                        </button>
                        <button onClick={() => handleListAdd(-1)} className="bg-pe-tile-header dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-muted dark:text-slate-300 font-bold border border-pe-border-strong dark:border-slate-600 rounded transition-all h-[44px]">
                            - Abziehen
                        </button>
                    </div>

                    <div className="bg-pe-tile-header dark:bg-[#08090b] rounded-lg border border-pe-border dark:border-slate-800 p-4 min-h-[120px] max-h-[200px] overflow-y-auto flex flex-col-reverse gap-1 text-left shadow-inner">
                        {entries.length === 0 ? <div className="text-center text-stone-400 dark:text-slate-600 text-sm italic self-center my-auto">Liste leer</div> : (
                            entries.map((val, idx) => (
                                <div key={idx} className="flex justify-between items-center border-b border-pe-border dark:border-slate-800 pb-1 last:border-0 hover:bg-stone-100 dark:hover:bg-slate-800/30 px-2 rounded text-left">
                                    <span className={`font-mono font-bold ${val < 0 ? 'text-red-600 dark:text-red-400' : 'text-pe-muted dark:text-slate-300'}`}>{val > 0 ? '+' : ''}{formatNumberOnly(val)} <span className="text-xs font-sans text-stone-500 dark:text-slate-500">{currency}</span></span>
                                    <button onClick={() => handleListRemove(idx)} className="text-stone-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-400 transition-colors px-2 py-1"><IconTrash /></button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="grid grid-cols-[1fr_auto] gap-4 items-center pt-2 border-t border-pe-border dark:border-slate-800 text-left">
                        <div className="flex gap-2">
                            <button onClick={() => handleListConvert(1/12)} className="bg-pe-tile-header dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-muted dark:text-slate-400 border border-pe-border-strong dark:border-slate-600 rounded px-3 py-2 text-xs font-bold transition-colors">÷ 12 (Mo)</button>
                            <button onClick={() => handleListConvert(12)} className="bg-pe-tile-header dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-muted dark:text-slate-400 border border-pe-border-strong dark:border-slate-600 rounded px-3 py-2 text-xs font-bold transition-colors">× 12 (J)</button>
                        </div>
                        <div className="flex flex-col items-end text-right">
                            <span className="text-xs text-stone-500 dark:text-slate-500 uppercase tracking-wider mb-1 font-bold">Summe</span>
                            <div className="flex items-baseline gap-1">
                                <span className="text-3xl font-bold text-pe-accent-text dark:text-[#E6C299] font-mono leading-none">{formatNumberOnly(listSum)}</span>
                                <span className="text-sm font-sans text-stone-500 dark:text-slate-500">{currency}</span>
                            </div>
                            {convRes !== null && (
                                <span className="text-sm text-blue-700 dark:text-blue-400 font-mono mt-1">
                                    = {formatNumberOnly(convRes)} {currency}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Kurzauswertung = ({ calc }: { calc: any }) => {
    const formatValue = (val: number, decimals: number = 0) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val || 0);

    const Card = ({ label, value, icon, color, isPercentage = false, isFactor = false, tooltip, decimalPlaces = 2 }:any) => (
        <div className={`bg-pe-card dark:bg-[#15171e] p-4 rounded-xl shadow-lg flex items-center border-l-4 ${color} border-y border-r border-pe-border dark:border-slate-800 text-left`} data-tooltip-id="app-tooltip" data-tooltip-content={tooltip}>
            <div className="mr-3 text-pe-muted dark:text-slate-400">{icon}</div>
            <div className="flex-1 text-left">
                <div className={`text-slate-500 uppercase font-bold text-[10px] tracking-wider`}>{label}</div>
                <div className={`font-bold text-pe-text dark:text-white text-lg flex items-baseline gap-1 font-mono-numbers`}>
                    {isPercentage ? `${formatValue(value, 1)}%` : isFactor ? formatValue(value, 2) : <>{formatValue(value, decimalPlaces)} <span className="text-sm text-stone-500 dark:text-slate-500 font-sans ml-1">{calc.currencyCode || 'EUR'}</span></>}
                </div>
            </div>
        </div>
    );

    // 💉 CHIRURGIE-FIX: DOPPEL-SCHLOSS-MATRIX INTEGRATION (Anpassung auf 5 Stufen)
    const rentabilityStatus = useMemo(() => {
        const gehalt = calc.gesamtlohn || 0; 
        const ueberschussProzent = calc.ueberschussProzent || 0;

        const evaluation = evaluateRentabilitaet(gehalt, ueberschussProzent);
        const base = { label: evaluation.status.toUpperCase(), message: evaluation.message };

        // Light: weiße Karte + farbiger Rand/Text (kein Gelb-Flächenlook). Dark unverändert.
        if (evaluation.status === 'Kritisch') return { ...base, color: 'text-red-800 dark:text-red-400', bgColor: 'bg-white dark:bg-red-950/20', borderColor: 'border-red-400 dark:border-red-900/50' };
        if (evaluation.status === 'Grenzwertig') return { ...base, color: 'text-[#8D6435] dark:text-amber-400', bgColor: 'bg-white dark:bg-amber-950/20', borderColor: 'border-[#C4A574] dark:border-amber-900/50' };
        if (evaluation.status === 'Solide') return { ...base, color: 'text-lime-800 dark:text-lime-400', bgColor: 'bg-white dark:bg-lime-950/20', borderColor: 'border-lime-500 dark:border-lime-900/50' };
        if (evaluation.status === 'Gut') return { ...base, color: 'text-emerald-800 dark:text-emerald-400', bgColor: 'bg-white dark:bg-emerald-950/20', borderColor: 'border-emerald-500 dark:border-emerald-900/50' };
        
        return { ...base, color: 'text-purple-800 dark:text-purple-400', bgColor: 'bg-white dark:bg-purple-950/20', borderColor: 'border-purple-400 dark:border-purple-900/50' };
    }, [calc.gesamtlohn, calc.ueberschussProzent]);

    const status = useMemo(() => {
        const ueberschussProzent = calc.ueberschussProzent || 0;
        const target = 10;
        if (ueberschussProzent >= target) return { text: 'Ziel erreicht', color: 'text-emerald-800 dark:text-emerald-400', icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>, bgColor: 'bg-white dark:bg-emerald-950/20', borderColor: 'border-emerald-500 dark:border-emerald-900/50' };
        if (ueberschussProzent > 0) return { text: 'Kostendeckend', color: 'text-[#8D6435] dark:text-amber-400', icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, bgColor: 'bg-white dark:bg-amber-950/20', borderColor: 'border-[#C4A574] dark:border-amber-900/50' };
        return { text: 'Verlust', color: 'text-red-800 dark:text-red-400', icon: <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 17h8m0 0V9m0 8l-8-8-4-4-6-6" /></svg>, bgColor: 'bg-white dark:bg-red-950/20', borderColor: 'border-red-400 dark:border-red-900/50' };
    }, [calc.ueberschussProzent]);

    return (
        <UI.Section className="flex flex-col gap-6 text-left">
            <UI.SectionTitle>Kurzauswertung</UI.SectionTitle>
            
            <p className="text-sm text-pe-muted dark:text-slate-400 font-medium leading-relaxed text-left -mt-2">
                Hierbei handelt es sich um den kalkulatorischen Startpunkt und somit um eine Prognose zu Jahresbeginn. Wie sich das Jahr tatsächlich entwickelt und hoffentlich verbessert, lässt sich auf den Leistungsseiten ersehen.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
                <Card label="Gemeinkosten" value={(calc.gemeinkostenfaktor || 0) * 100} isPercentage={true} color="border-cyan-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>} />
                <Card label="Material" value={(calc.materialkostenfaktor || 0) * 100} isPercentage={true} color="border-teal-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>} />
                <Card label="Geräte" value={(calc.geraetekostenfaktor || 0) * 100} isPercentage={true} color="border-slate-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>} />
                <Card label="Lohn (prod.)" value={(calc.prodLohnkostenfaktor || 0) * 100} isPercentage={true} color="border-indigo-500" icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <Card label="Gesamtumsatz (netto)" value={calc.monatsumsatzNetto} color="border-emerald-500" decimalPlaces={0} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                <Card label="Gesamtkosten (Sollumsatz)" value={Number(calc.foerderzuschussMonat) > 0 ? calc.breakEvenNachFoerderung : calc.sollumsatz} color="border-red-500" decimalPlaces={0} icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>} />
            </div>
            {Number(calc.foerderzuschussMonat) > 0 && (
                <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium -mt-2">
                    Staatlicher Förderzuschuss: −{formatValue(calc.foerderzuschussMonat, 0)} {calc.currencyCode || 'EUR'}.
                    Kosten vor Abzug {formatValue(calc.sollumsatzVorFoerderung || calc.sollumsatz, 0)} {calc.currencyCode || 'EUR'}.
                    Azubi und produktive Fachkraft werden nur am Schluss vom Sollumsatz abgezogen. Der Team-Stundenlohn bleibt voll. Unproduktive Förderung kommt aus den Gemeinkosten.
                </p>
            )}

            <div className="space-y-4 mt-2 text-left">
                <div className={`rounded-xl p-5 shadow-lg flex items-center border ${status.borderColor} ${status.bgColor} w-full text-left`}>
                    <div className={`flex-shrink-0 mr-6 ${status.color} scale-125`}>{status.icon}</div>
                    <div className="flex-1 text-left">
                        <div className="text-xs text-pe-muted dark:text-slate-400 uppercase font-bold tracking-widest text-left mb-1">kalk. Überschuss / Verlust</div>
                        <div className="flex justify-start items-baseline space-x-3 text-left">
                            <span className={`text-4xl font-black font-mono-numbers ${status.color}`}>{formatValue(calc.ueberschuss, 0)}</span>
                            <span className="text-xl text-stone-500 dark:text-slate-500 font-bold uppercase">{calc.currencyCode || 'EUR'}</span>
                            <span className={`text-xl font-black font-mono-numbers ${status.color} opacity-80 ml-2`}>({formatValue(calc.ueberschussProzent || 0, 1)}%)</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] text-stone-500 dark:text-slate-500 uppercase font-bold tracking-widest">Ziel: mind. 10%</div>
                        <div className={`font-black tracking-widest uppercase text-xl mt-1 ${status.color}`}>{status.text}</div>
                    </div>
                </div>

                {/* 💉 CHIRURGIE-FIX: DOPPEL-SCHLOSS-MATRIX UI INTEGRATION */}
                <div className={`rounded-xl p-5 shadow-lg flex flex-col border ${rentabilityStatus.borderColor} ${rentabilityStatus.bgColor} w-full transition-all duration-300 text-left`}>
                    <div className="flex items-center w-full">
                        <div className={`flex-shrink-0 mr-6 ${rentabilityStatus.color} scale-110`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        </div>
                        <div className="flex-1 text-left">
                            <div className="text-[10px] text-pe-muted dark:text-slate-400 uppercase font-bold tracking-widest text-left mb-1">Kalkulatorische Rentabilität</div>
                            <div className="flex justify-start items-baseline space-x-2 text-left">
                                <span className={`text-3xl font-black font-mono-numbers ${rentabilityStatus.color}`}>{formatValue(calc.rentabilitaetSoll, 1)}%</span>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] text-stone-500 dark:text-slate-500 uppercase font-bold tracking-widest">Status</div>
                            <div className={`font-black text-sm uppercase tracking-widest mt-1 ${rentabilityStatus.color}`}>{rentabilityStatus.label}</div>
                        </div>
                    </div>
                    {/* Neue UI-Zeile für die Dynamische Message */}
                    <div className={`mt-4 pt-3 border-t ${rentabilityStatus.borderColor} text-xs font-medium leading-relaxed ${rentabilityStatus.color} opacity-90`}>
                        {rentabilityStatus.message}
                    </div>
                </div>
                
                <div className="bg-pe-elevated dark:bg-[#0f1015] border border-pe-border dark:border-slate-800 rounded-xl p-5 text-left">
                    <div className="flex items-start gap-4">
                        <div className="text-blue-700 dark:text-blue-400 mt-1 shrink-0">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                        </div>
                        <div className="text-xs text-pe-muted dark:text-slate-400 leading-relaxed font-medium text-left">
                            Kalkulatorischer Inhaber-Ertrag von <span className="text-pe-accent-text dark:text-[#E6C299] font-bold">{formatValue(calc.inhaberErtragSoll, 0)} {calc.currencyCode || 'EUR'} pro Monat</span> (vor Steuern). 
                            Der Wert beinhaltet Deinen Überschuss, Entnahmen und kalkulierte Lohnsteuer. 
                            <span className="block mt-1 text-blue-700 dark:text-blue-400 font-bold">Bespreche die finale Steuerlast bitte mit Deinem Steuerberater.</span>
                        </div>
                    </div>
                </div>
            </div>
        </UI.Section>
    );
};

const MitarbeiterUmsatzprognose = ({ calc }: { calc: any }) => {
    const { updateBwaData, updatePrognoseZeile } = useAppStore();
    const bruttolohn = calc.bwaData.prognoseBruttolohn || 0;
    const faktor = calc.bwaData.prognoseFaktor || 2.5;
    const monatsumsatzNetto = (bruttolohn * faktor);
    const jahresumsatzNettoBereinigt = (monatsumsatzNetto * 10) / (1 + calc.mwstSatz / 100);
    const faktorOptions = Array.from({ length: 16 }, (_, i) => (1.5 + i * 0.1).toFixed(1));
    const currency = calc.currencyCode || 'EUR';

    const syncFirstRow = (newLohn: number, newFaktor: number) => {
        if (calc.bwaData.prognoseZeilen?.length === 1) {
            updatePrognoseZeile(0, 'lohn', newLohn);
            updatePrognoseZeile(0, 'faktor', newFaktor);
            const autoJahresumsatz = (newLohn * newFaktor * 10) / (1 + calc.mwstSatz / 100);
            updatePrognoseZeile(0, 'jahresumsatz', autoJahresumsatz);
        }
    };

    return (
        <div className="mb-6 relative text-left">
            {calc.loading && <div className="absolute inset-0 bg-[#F4F2EF]/50 dark:bg-[#0f1015]/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E6C299]"></div></div>}
            <h4 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-4 text-center">Mitarbeiter Umsatzprognose (Basiswerte)</h4>
            <div className="overflow-x-auto bg-pe-elevated dark:bg-[#0f1015] p-4 rounded-xl border border-pe-border dark:border-slate-800 text-left shadow-lg">
                <div className="grid grid-cols-4 gap-4 items-center p-2 text-center min-w-[700px] text-left">
                    <div className="font-bold text-[10px] uppercase tracking-widest text-stone-500 dark:text-slate-500">Bruttolohn</div>
                    <div className="font-bold text-[10px] uppercase tracking-widest text-stone-500 dark:text-slate-500">Faktor</div>
                    <div className="font-bold text-[10px] uppercase tracking-widest text-[#8D6435] dark:text-[#E6C299]">kalk. Brutto-Monatsumsatz</div>
                    <div className="font-bold text-[10px] uppercase tracking-widest text-emerald-800 dark:text-emerald-400">kalk. Netto-Jahresumsatz (Urlaub ber.)</div>
                    
                    <div className="text-left"><EditableField value={bruttolohn} onChange={(val:number) => { updateBwaData('prognoseBruttolohn', val); syncFirstRow(val, faktor); }} type="currency" currency={currency} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers h-10" /></div>
                    <div className="text-left"><UI.Select value={faktor} onChange={(e) => { const f = parseFloat(e.target.value); updateBwaData('prognoseFaktor', f); syncFirstRow(bruttolohn, f); }} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers h-10">{faktorOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</UI.Select></div>
                    <div className="text-left"><EditableField value={monatsumsatzNetto} readOnly transparent type="currency" currency={currency} className="font-black text-[#8D6435] dark:text-[#E6C299] font-mono-numbers h-10" /></div>
                    <div className="text-left"><EditableField value={jahresumsatzNettoBereinigt} readOnly transparent type="currency" currency={currency} className="font-black text-emerald-800 dark:text-emerald-400 font-mono-numbers h-10" /></div>
                </div>
            </div>
        </div>
    );
};

const PrognoseAktivierung = ({ calc }: { calc: any }) => {
    const { addPrognoseZeile, updatePrognoseZeile, removePrognoseZeile } = useAppStore();
    const prognoseZeilen = calc.bwaData.prognoseZeilen || [];
    const dateOptions = useMemo(() => { const opts = []; const d = new Date(); const y = d.getFullYear(); for (let i = y; i < y + 5; i++) { for (let m = 1; m <= 12; m++) { const dt = new Date(i, m - 1, 1); opts.push({ value: dt.toISOString().split('T')[0], label: `${dt.toLocaleString('de-DE', { month: 'long' })} ${i}` }); } } return opts; }, []);
    const currency = calc.currencyCode || 'EUR';
    const faktorOptions = Array.from({ length: 16 }, (_, i) => (1.5 + i * 0.1).toFixed(1));
    
    return (
        <div id="prognose-section" className="relative text-left"> 
            {calc.loading && <div className="absolute inset-0 bg-[#F4F2EF]/50 dark:bg-[#0f1015]/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl"></div>}
             <h4 className="text-sm font-black text-pe-text dark:text-white uppercase tracking-widest mb-4 text-center">Prognose Aktivierung</h4>
            <div className="overflow-x-auto bg-pe-elevated dark:bg-[#0f1015] p-4 rounded-xl border border-pe-border dark:border-slate-800 text-left shadow-lg">
                <table className="min-w-[900px] w-full text-center text-left">
                    <thead>
                        <tr className="border-b border-pe-border dark:border-slate-800">
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 text-left">Mitarbeiter</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 text-left">Lohn</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 text-left">Faktor</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 text-left">progn. Jahresumsatz</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 text-left">Startdatum</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] text-center">Aktiv</th>
                            <th className="p-3 text-[10px] font-bold uppercase tracking-widest text-red-700/80 dark:text-red-500/70 text-center">Löschen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {prognoseZeilen.map((zeile: any, index: number) => (
                            <tr key={zeile.id} className="border-b border-pe-border dark:border-slate-800/50 py-2 hover:bg-stone-100 dark:hover:bg-slate-800/20 transition-colors">
                                <td className="p-2 w-1/6 text-left"><UI.Select value={zeile.mitarbeiterId} onChange={(e) => updatePrognoseZeile(index, 'mitarbeiterId', e.target.value)} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 text-xs h-10"><option value="">Wählen...</option>{(calc.personalData || []).map((p:any) => <option key={p.id} value={p.id}>{p.name}</option>)}</UI.Select></td>
                                <td className="p-2 w-1/6 text-left"><EditableField value={zeile.lohn} onChange={(newVal:number) => updatePrognoseZeile(index, 'lohn', newVal)} type="currency" currency={currency} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers text-xs h-10" /></td>
                                <td className="p-2 w-1/6 text-left"><UI.Select value={zeile.faktor} onChange={(e) => updatePrognoseZeile(index, 'faktor', parseFloat(e.target.value))} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers text-xs h-10">{faktorOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</UI.Select></td>
                                <td className="p-2 w-1/6 text-left"><EditableField value={zeile.jahresumsatz} onChange={(val:number) => updatePrognoseZeile(index, 'jahresumsatz', val)} type="currency" currency={currency} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers font-bold text-xs h-10" /></td>
                                <td className="p-2 w-1/6 text-left"><UI.Select value={zeile.datum.split('T')[0]} onChange={(e) => updatePrognoseZeile(index, 'datum', e.target.value)} className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers text-xs h-10">{dateOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</UI.Select></td>
                                <td className="p-2 text-center"><div className="flex justify-center items-center h-full"><input type="checkbox" checked={zeile.aktiv} onChange={(e) => updatePrognoseZeile(index, 'aktiv', e.target.checked)} className="form-checkbox h-5 w-5 text-pe-accent-text dark:text-[#E6C299] bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 rounded transition-colors focus:ring-[#E6C299]" /></div></td>
                                <td className="p-2 text-center"><button onClick={() => removePrognoseZeile(zeile.id)} className="text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 p-2 rounded-lg hover:bg-red-100/60 dark:hover:bg-red-950/30 transition-colors"><IconTrash /></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="mt-4 flex items-center gap-4 text-left">
                <button onClick={() => addPrognoseZeile({ lohn: calc.bwaData.prognoseBruttolohn, faktor: calc.bwaData.prognoseFaktor })} className="bg-pe-tile-header dark:bg-slate-800 hover:bg-pe-hover dark:hover:bg-slate-700 text-pe-text dark:text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-lg border border-pe-border-strong dark:border-slate-700 transition-colors">
                    + Zeile hinzufügen
                </button>
                {calc.showSuccessMessage && <span className="text-emerald-700 dark:text-emerald-400 text-xs font-bold tracking-widest uppercase animate-pulse">Gespeichert!</span>}
            </div>
        </div>
    );
};

const BwaUmsatzAbgleich = ({ calc }: { calc: any }) => {
    const { updateBwaData } = useAppStore();
    const currency = calc.currencyCode || 'EUR';
    const mwstRate = calc.mwstSatz !== undefined && calc.mwstSatz !== null ? Number(calc.mwstSatz) : 19;
    const mwstFaktor = 1 + (mwstRate / 100);
    const zeitraum = calc.bwaData.zeitraum || 12;

    const globalFaktor = calc.bwaData.abgleichGlobalFaktor || 2.5;

    const unternehmerUmsatzBruttoMonat = calc.kalkulierterUnternehmerUmsatzBrutto || 0;
    const unternehmerUmsatzNettoMonat = unternehmerUmsatzBruttoMonat / mwstFaktor;

    const personalData = Array.isArray(calc.personalData) ? calc.personalData : [];
    
    const produktiveMa = useMemo(() => {
        return personalData.filter((ma: any) => {
            const isAzubi = ['Azubi', 'Prod. Azubi', 'produktiver Azubi'].includes(ma.taetigkeit || '');
            const isUnproduktiv = ma.type === 'unproduktiv' || ma.isProduktiv === false;
            return !isAzubi && !isUnproduktiv;
        });
    }, [personalData]);

    const maBerechnungen = useMemo(() => {
        return produktiveMa.map((p: any) => {
            const lohn = p.grundlohn || 0;
            const bruttoMonat = lohn * globalFaktor;
            const nettoMonat = bruttoMonat / mwstFaktor;
            
            return {
                id: p.id,
                name: p.name || 'Unbekannt',
                lohn,
                bruttoMonat,
                nettoMonat
            };
        }).filter((m: any) => m.lohn > 0);
    }, [produktiveMa, globalFaktor, mwstFaktor]);

    const maSummeBruttoMonat = maBerechnungen.reduce((sum: number, p: any) => sum + p.bruttoMonat, 0);
    const maSummeNettoMonat = maBerechnungen.reduce((sum: number, p: any) => sum + p.nettoMonat, 0);

    const gesamtSollBruttoMonat = maSummeBruttoMonat + unternehmerUmsatzBruttoMonat;
    const gesamtSollNettoMonat = maSummeNettoMonat + unternehmerUmsatzNettoMonat;

    const istUmsatzNettoJahr = calc.bwaData.umsatz || 0;
    const istUmsatzNettoMonat = zeitraum > 0 ? istUmsatzNettoJahr / zeitraum : 0;
    const differenzNettoMonat = istUmsatzNettoMonat - gesamtSollNettoMonat;
    
    const istUmsatzBruttoMonat = istUmsatzNettoMonat * mwstFaktor;
    const differenzBruttoMonat = differenzNettoMonat * mwstFaktor;

    return (
        <div className="bg-pe-elevated dark:bg-[#0f1015] p-6 rounded-xl border border-pe-border dark:border-slate-800 text-left mt-2 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-pe-card dark:bg-[#15171e] p-5 rounded-xl border border-pe-border dark:border-slate-800 shadow-inner">
                    <label className="text-[10px] text-stone-500 dark:text-slate-500 font-bold uppercase tracking-widest block mb-3 flex items-center gap-2">
                        <Calculator size={14} className="text-pe-accent-text dark:text-[#E6C299]" />
                        Globaler Umsatz-Faktor
                    </label>
                    <div className="w-1/2">
                        <EditableField value={globalFaktor} onChange={(v:number) => updateBwaData('abgleichGlobalFaktor', v)} type="number" className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 text-pe-accent-text dark:text-[#E6C299] font-mono-numbers h-10" />
                    </div>
                    <p className="text-[10px] text-stone-500 dark:text-slate-500 mt-3 font-medium">Faktor, mit dem der Lohn multipliziert wird (Standard 2,5).</p>
                </div>
                <div className="bg-pe-tile-header dark:bg-[#08090b] p-5 rounded-xl border border-pe-border dark:border-slate-800 shadow-inner opacity-80 cursor-not-allowed">
                    <label className="text-[10px] text-stone-400 dark:text-slate-600 font-bold uppercase tracking-widest block mb-3 flex items-center gap-2">
                        <User size={14} className="text-slate-600" />
                        Geplanter Unternehmer-Umsatz (Netto/Monat)
                    </label>
                    <div className="font-mono-numbers text-2xl font-black text-pe-muted dark:text-slate-400">
                        {formatIntCurrency(unternehmerUmsatzNettoMonat, currency)}
                    </div>
                    <p className="text-[10px] text-stone-400 dark:text-slate-600 mt-3 font-medium">Wird automatisch aus deinen Stammdaten bezogen.</p>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-pe-border dark:border-slate-800 shadow-sm mt-8">
                <table className="w-full text-left border-collapse bg-pe-card dark:bg-[#15171e]">
                    <thead>
                        <tr className="bg-pe-tile-header dark:bg-[#08090b] border-b border-pe-border dark:border-slate-800">
                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500">Umsatz-Quelle</th>
                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] text-right">Kalk. Netto / Monat</th>
                            <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-stone-400 dark:text-slate-600 text-right">Kalk. Brutto / Monat</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="bg-stone-200/60 dark:bg-slate-800/30 border-b border-pe-border-strong dark:border-slate-700">
                            <td className="p-4 text-[10px] uppercase tracking-widest font-black text-pe-muted dark:text-slate-400">Summe Team-Umsatz (Alle prod. MA)</td>
                            <td className="p-4 text-sm font-mono-numbers font-black text-pe-accent-text dark:text-[#E6C299] text-right">{formatIntCurrency(maSummeNettoMonat, currency)}</td>
                            <td className="p-4 text-sm font-mono-numbers font-bold text-stone-500 dark:text-slate-500 text-right">{formatIntCurrency(maSummeBruttoMonat, currency)}</td>
                        </tr>
                        <tr className="bg-pe-tile-header dark:bg-[#08090b]">
                            <td className="p-4 text-[10px] uppercase tracking-widest font-black text-stone-500 dark:text-slate-500 flex items-center gap-2">
                                + Unternehmer-Umsatz
                            </td>
                            <td className="p-4 text-sm font-mono-numbers font-black text-pe-text dark:text-white text-right">{formatIntCurrency(unternehmerUmsatzNettoMonat, currency)}</td>
                            <td className="p-4 text-sm font-mono-numbers font-bold text-stone-400 dark:text-slate-600 text-right">{formatIntCurrency(unternehmerUmsatzBruttoMonat, currency)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mt-8 p-6 bg-pe-tile-header dark:bg-[#08090b] border border-pe-border dark:border-slate-800 rounded-xl shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299]"></div>
                
                <h4 className="text-xs font-black text-pe-text dark:text-white uppercase tracking-widest mb-6 flex items-center gap-2">
                    BWA Differenz-Auswertung <span className="text-slate-500 font-medium normal-case tracking-normal">(Monatsbasis, Netto)</span>
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    <div className="flex flex-col">
                        <span className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-bold block mb-1">Soll-Erwartung / Monat</span>
                        <div className="flex flex-wrap items-baseline gap-3">
                            <span className="text-3xl font-mono-numbers font-black text-pe-accent-text dark:text-[#E6C299] tracking-tight">{formatIntCurrency(gesamtSollNettoMonat, currency)}</span>
                            <span className="text-[10px] text-stone-500 dark:text-slate-500 font-mono-numbers font-bold bg-pe-card dark:bg-[#15171e] px-2 py-1 rounded border border-pe-border dark:border-slate-800">Brutto: {formatIntCurrency(gesamtSollBruttoMonat, currency)}</span>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <span className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-bold block mb-1">Ist-Umsatz / Monat (Lt. BWA)</span>
                        <div className="flex flex-wrap items-baseline gap-3">
                            <span className="text-3xl font-mono-numbers font-black text-pe-text dark:text-white tracking-tight">{formatIntCurrency(istUmsatzNettoMonat, currency)}</span>
                            <span className="text-[10px] text-stone-500 dark:text-slate-500 font-mono-numbers font-bold bg-pe-card dark:bg-[#15171e] px-2 py-1 rounded border border-pe-border dark:border-slate-800">Brutto: {formatIntCurrency(istUmsatzBruttoMonat, currency)}</span>
                        </div>
                    </div>

                    <div className="bg-pe-card dark:bg-[#15171e] p-4 rounded-xl border border-pe-border dark:border-slate-800 shadow-inner flex flex-col justify-center">
                        <span className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-bold block mb-1">Differenz pro Monat</span>
                        <div className="flex flex-wrap items-baseline gap-3">
                            <span className={`text-3xl font-mono-numbers font-black tracking-tight ${differenzNettoMonat >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                {differenzNettoMonat >= 0 ? '+' : ''}{formatIntCurrency(differenzNettoMonat, currency)}
                            </span>
                            <span className={`text-[10px] font-mono-numbers font-bold bg-pe-card dark:bg-[#08090b] px-2 py-1 rounded border border-pe-border dark:border-slate-800 ${differenzNettoMonat >= 0 ? 'text-emerald-700 dark:text-emerald-500/50' : 'text-red-600 dark:text-red-500/50'}`}>
                                Brutto: {differenzBruttoMonat >= 0 ? '+' : ''}{formatIntCurrency(differenzBruttoMonat, currency)}
                            </span>
                        </div>
                        <p className="text-[10px] text-pe-muted dark:text-slate-400 mt-2 font-medium leading-tight">
                            {differenzNettoMonat >= 0 
                                ? "Der reale Monatsumsatz übersteigt die errechnete Faktor-Erwartung." 
                                : "Achtung: Der reale BWA Monatsumsatz liegt unter der Erwartung."}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const KostenstrukturPage = () => {
    const appStore = useAppStore() as any;
    const { updateBwaData, setClientData, updateYearBwaUrl } = appStore;
    const calc = useCalculationsSelector(); 
    const currency = calc.currencyCode || 'EUR'; 
    const currentVisibleCount = calc.bwaData.visibleGeraeteCount ?? 4;

    const currentYear = localStorage.getItem('CURRENT_YEAR') || new Date().getFullYear().toString();
    const { impersonatedClientId } = useAdminStore();
    const [liveBwaList, setLiveBwaList] = useState<string[]>([]);

    useEffect(() => {
        const safeId = (impersonatedClientId === "undefined" || impersonatedClientId === "null") ? null : impersonatedClientId;
        const uid = safeId || auth.currentUser?.uid;
        if (!uid || !currentYear) return;
        
        const yearDocRef = doc(db, 'kalkulation_mandanten', uid, 'jahre', currentYear);
        const unsub = onSnapshot(yearDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const urls = data.bwaUrls || data.clientData?.bwaUrls || 
                             (data.bwaUrl ? [data.bwaUrl] : (data.clientData?.bwaUrl ? [data.clientData.bwaUrl] : []));
                
                if (Array.isArray(urls)) {
                    setLiveBwaList(urls.filter(url => typeof url === 'string' && url.trim() !== ''));
                }
            }
        }, (error) => {
            console.error("FATAL: Listener abgestürzt, Loop verhindert:", error);
        });

        return () => unsub();
    }, [impersonatedClientId, currentYear]);

    const activeYearData = appStore.clientData?.geschaeftsjahre?.find((j: any) => String(j.jahr) === currentYear) 
                        || appStore.userData?.geschaeftsjahre?.find((j: any) => String(j.jahr) === currentYear);
    const rawBwaUrls = activeYearData?.bwaUrls || calc.bwaUrls || calc.bwaData?.bwaUrls;
    const rawBwaSingle = activeYearData?.bwaUrl || calc.bwaUrl || calc.bwaData?.bwaUrl;
    
    let bwaList: string[] = [];
    if (liveBwaList.length > 0) {
        bwaList = liveBwaList;
    } else if (Array.isArray(rawBwaUrls) && rawBwaUrls.length > 0) {
        bwaList = rawBwaUrls;
    } else if (typeof rawBwaSingle === 'string' && rawBwaSingle.trim() !== '') {
        bwaList = [rawBwaSingle];
    } else if (Array.isArray(rawBwaSingle) && rawBwaSingle.length > 0) {
        bwaList = rawBwaSingle;
    }

    const handleOpenBwa = (targetUrl: string) => {
        if (!targetUrl) return;
        const cleanUrl = targetUrl + '&view=FitH#toolbar=0&navpanes=0';
        window.open(cleanUrl, '_blank', 'width=1000,height=900,left=100,top=100,resizable=yes,scrollbars=yes');
    };

    const [isUploading, setIsUploading] = useState(false);
    const [isDeletingUrl, setIsDeletingUrl] = useState<string | null>(null);
    const [yearNeedingUpload, setYearNeedingUpload] = useState<number | null>(null);
    const [showBwaModal, setShowBwaModal] = useState(false);
    const [bwaScaleConfirm, setBwaScaleConfirm] = useState<null | {
        mode: 'up' | 'down';
        fromMonths: number;
        toMonths: number;
    }>(null);

    const jahre = appStore.clientData?.geschaeftsjahre || [];
    const targetYearForModal = jahre.find((j: any) => String(j.jahr) === String(yearNeedingUpload));
    const modalHasBwa = targetYearForModal?.bwaUrl || (targetYearForModal?.bwaUrls && targetYearForModal.bwaUrls.length > 0);
    const modalBwaList = targetYearForModal?.bwaUrls || (targetYearForModal?.bwaUrl ? [targetYearForModal.bwaUrl] : []);

    const handleLateBwaUpload = async (e: React.ChangeEvent<HTMLInputElement>, targetYear: number | null) => {
        if (!targetYear) return;

        try {
            const files = Array.from(e.target.files || []);
            if (files.length === 0) return;

            const allValid = files.every(f => f.type === 'application/pdf');
            if (!allValid) {
                alert("Bitte lade ausschließlich originale PDF-Dokumente (keine Fotos) hoch.");
                if (e.target) e.target.value = '';
                return;
            }

            setIsUploading(true);
            
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const newUrls: string[] = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const timestamp = Date.now();
                const fileRef = ref(storage, `kalkulation_mandanten/${safeUid}/uploads/BWA_${safeYear}_${timestamp}_${i}.pdf`);
                
                const metadata = { contentType: 'application/pdf' };
                const snapshot = await uploadBytes(fileRef, file, metadata);
                const downloadUrl = await getDownloadURL(snapshot.ref);
                newUrls.push(downloadUrl);
            }

            const finalUrls = [...existingBwaUrls, ...newUrls];

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: finalUrls[0], bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: finalUrls[0],
                bwaUrls: finalUrls,
                "clientData.bwaUrl": finalUrls[0],
                "clientData.bwaUrls": finalUrls
            }, { merge: true });

            if (typeof setClientData === 'function') {
                setClientData((prev: any) => {
                    if (!prev) return prev;
                    return { ...prev, geschaeftsjahre: updatedJahre };
                });
            }

            if (typeof updateYearBwaUrl === 'function') {
                updateYearBwaUrl(targetYear, finalUrls[0]);
            }

            alert(`${files.length} BWA-Dokument(e) für ${targetYear} erfolgreich hochgeladen!`);
        } catch (err) {
            console.error("Upload Fehler:", err);
            alert("Fehler beim Upload.");
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleDeleteSingleBwa = async (urlToDelete: string, targetYear: number | null) => {
        if (!targetYear || !urlToDelete) return;
        if (!window.confirm("Dieses PDF wirklich unwiderruflich löschen?")) return;

        setIsDeletingUrl(urlToDelete);
        try {
            const user = auth.currentUser;
            const targetUid = impersonatedClientId || user?.uid;
            if (!targetUid) throw new Error("Keine Ziel-ID gefunden");

            const safeUid = String(targetUid);
            const safeYear = String(targetYear);

            try {
                const fileRef = ref(storage, urlToDelete);
                await deleteObject(fileRef);
            } catch (storageErr) {
                console.warn("Storage-Löschung fehlgeschlagen (evtl. schon gelöscht):", storageErr);
            }

            const docRef = doc(db, 'kalkulation_mandanten', safeUid);
            const freshSnap = await getDoc(docRef);
            const freshData = freshSnap.exists() ? freshSnap.data() : {};
            const currentYears = freshData?.clientData?.geschaeftsjahre || freshData?.geschaeftsjahre || [];
            
            const targetYearData = currentYears.find((j: any) => Number(j.jahr) === Number(targetYear));
            const existingBwaUrls = targetYearData?.bwaUrls || (targetYearData?.bwaUrl ? [targetYearData.bwaUrl] : []);
            
            const finalUrls = existingBwaUrls.filter((u: string) => u !== urlToDelete);
            const newMainUrl = finalUrls.length > 0 ? finalUrls[0] : '';

            const updatedJahre = currentYears.map((j: any) => 
                Number(j.jahr) === Number(targetYear) ? { ...j, bwaUrl: newMainUrl, bwaUrls: finalUrls } : j
            );

            await updateDoc(docRef, {
                "clientData.geschaeftsjahre": updatedJahre,
                geschaeftsjahre: updatedJahre
            });

            const yearDocRef = doc(db, 'kalkulation_mandanten', safeUid, 'jahre', safeYear);
            await setDoc(yearDocRef, {
                bwaUrl: newMainUrl,
                bwaUrls: finalUrls,
                "clientData.bwaUrl": newMainUrl,
                "clientData.bwaUrls": finalUrls
            }, { merge: true });

            if (typeof setClientData === 'function') {
                setClientData((prev: any) => {
                    if (!prev) return prev;
                    return { ...prev, geschaeftsjahre: updatedJahre };
                });
            }

            alert("PDF erfolgreich gelöscht.");
        } catch (err) {
            console.error("Fehler beim Löschen:", err);
            alert("Fehler beim Löschen der Datei.");
        } finally {
            setIsDeletingUrl(null);
        }
    };

    const activePrognoseUmsatz = useMemo(() => {
        return (calc.bwaData.prognoseZeilen || [])
            .filter((z: any) => z.aktiv)
            .reduce((sum: number, z: any) => sum + (z.jahresumsatz || 0), 0);
    }, [calc.bwaData.prognoseZeilen]);

    const isPrognoseActive = activePrognoseUmsatz > 0;

    const handleDeleteGeraet = (index: number) => {
        if (currentVisibleCount <= 1) return;
        const key = `geraetEintrag${index + 1}`;
        updateBwaData(key, 0);
        updateBwaData(`${key}_label`, '');
        updateBwaData('visibleGeraeteCount', currentVisibleCount - 1);
    };

    const scaleBwaValues = (bwa: any, fromMonths: number, toMonths: number) => {
        const nextBwa = { ...bwa };
        BWA_SCALE_KEYS.forEach((key) => {
            const raw = Number(bwa[key]);
            if (!Number.isFinite(raw)) return;
            if (key === 'premiumSoftware' && raw === 0) return;
            if (key.startsWith('geraetEintrag') && raw === 0) return;
            nextBwa[key] = Math.round((raw / fromMonths) * toMonths);
        });
        return nextBwa;
    };

    /** Manuell: N→12 hochrechnen ODER 12→N zurücklösen (kein Auto bei Zeitraum-Select). */
    const handleToggleBwaScaleTo12 = () => {
        const bwa = calc.bwaData || {};
        const current = Number(bwa.zeitraum) || 0;
        const sourceMonths = Number(bwa.zeitraumVorHochrechnung) || 0;
        const isScaled =
            current === 12 && sourceMonths >= 1 && sourceMonths <= 11;

        if (isScaled) {
            setBwaScaleConfirm({ mode: 'down', fromMonths: 12, toMonths: sourceMonths });
            return;
        }

        if (current < 1 || current >= 12) return;
        setBwaScaleConfirm({ mode: 'up', fromMonths: current, toMonths: 12 });
    };

    const executeBwaScaleConfirm = () => {
        if (!bwaScaleConfirm || typeof setClientData !== 'function') return;
        const { mode, fromMonths, toMonths } = bwaScaleConfirm;

        setClientData((prev: any) => {
            if (!prev) return prev;
            const prevBwa = prev.bwaData || {};
            const nextBwa = scaleBwaValues(prevBwa, fromMonths, toMonths);
            nextBwa.zeitraum = toMonths;
            if (mode === 'up') {
                nextBwa.zeitraumVorHochrechnung = fromMonths;
            } else {
                delete nextBwa.zeitraumVorHochrechnung;
            }
            return { ...prev, bwaData: nextBwa };
        });
        setBwaScaleConfirm(null);
    };

    /** Zeitraum manuell ändern: nur erlaubt, wenn keine aktive Hochrechnung. */
    const handleZeitraumChange = (nextZeitraum: number) => {
        const bwa = calc.bwaData || {};
        const sourceMonths = Number(bwa.zeitraumVorHochrechnung) || 0;
        const current = Number(bwa.zeitraum) || 0;
        // Nach Hochrechnung: Select gesperrt — Zeitraum nur via „Zurück“ ändern
        if (current === 12 && sourceMonths >= 1 && sourceMonths <= 11) return;
        updateBwaData('zeitraum', nextZeitraum);
    };

    const bwaZeitraum = Number(calc.bwaData?.zeitraum) || 12;
    const bwaScaleSourceMonths = Number(calc.bwaData?.zeitraumVorHochrechnung) || 0;
    const isBwaScaledTo12 =
        bwaZeitraum === 12 && bwaScaleSourceMonths >= 1 && bwaScaleSourceMonths <= 11;
    const canToggleBwaScale =
        isBwaScaledTo12 || (bwaZeitraum >= 1 && bwaZeitraum < 12);

    const bwaFieldGroups = useMemo(() => [
        { title: "Betriebskosten (orange Kosten sind variabel)", key: 'betriebskosten', color: "bg-stone-200 dark:bg-[#1e293b]", fields: [ { key: 'raumkosten', label: 'Raumkosten' }, { key: 'betriebssteuern', label: 'Betriebliche Steuern' }, { key: 'versicherungen', label: 'Versicherungen/Beiträge' }, { key: 'besondereKosten', label: 'besondere Kosten' }, { key: 'kfzKosten', label: 'KFZ Kosten' }, { key: 'werbeReisekosten', label: 'Werbe/Reisekosten', isOrange: true }, { key: 'warenabgabe', label: 'Warenabgabe an MA' }, { key: 'reparatur', label: 'Reparatur/Instandhaltung', isOrange: true }, { key: 'sonstigeKosten', label: 'sonstige Kosten', isOrange: true }, { key: 'kredite', label: 'Kredite, Zinsen u.ä.' }, { key: 'zinsaufwendungen', label: 'Zinsaufwendungen' }, { key: 'gewerbesteuer', label: 'Gewerbesteuer' }, { key: 'koerperschaftssteuer', label: 'Körperschaftssteuer (GmbH)' }, { key: 'budgetPlansumme', label: 'Budget Plansumme' }, { key: 'sonstigeEintrag1', labelKey: 'sonstigeEintrag1_label', isLabelEditable: true, placeholder: 'sonstige Einträge' }, { key: 'sonstigeEintrag2', labelKey: 'sonstigeEintrag2_label', isLabelEditable: true, placeholder: 'sonstige Einträge' }, { key: 'sonstigeEintrag3', labelKey: 'sonstigeEintrag3_label', isLabelEditable: true, placeholder: 'sonstige Einträge' }, { key: 'sonstigeEintrag4', labelKey: 'sonstigeEintrag4_label', isLabelEditable: true, placeholder: 'sonstige Einträge' } ]},
        { title: "positive Werte", key: 'positiveWerte', color: "bg-emerald-100 dark:bg-emerald-900/40", fields: [ { key: 'abschreibungen', label: 'Abschreibungen' }, { key: 'zinsenMiete', label: 'Sonstige Einnahmen' } ]},
        { title: "PreisExpert Beratung", key: 'preisExpertBeratung', color: "bg-[#E6C299]/10", fields: [ { key: 'preisExpert', label: 'PreisExpert Beratung', isCalculated: true } ] },
        { 
            title: "Kosmetische Geräte (Leasing/Kosten/Miete)", 
            key: 'geraete', 
            color: "bg-indigo-100 dark:bg-indigo-900/30", 
            isAggregated: true, 
            fields: Array.from({ length: currentVisibleCount }, (_, i) => ({
                key: `geraetEintrag${i + 1}`,
                labelKey: `geraetEintrag${i + 1}_label`,
                isLabelEditable: true,
                placeholder: 'Gerät eintragen',
                isDevice: true,
                index: i
            }))
        }
    ], [currentVisibleCount]);

    if (!calc.ready) return <div className="bg-pe-bg dark:bg-[#08090b] min-h-screen p-8 text-center text-pe-text dark:text-white">Lade Kostenstruktur...</div>;
    
    const renderRow = (field: any) => {
        let jahreswert = calc.bwaData[field.key] || 0;
        
        let isProBonoBadge = false;
        let proBonoUntilText = '';

        if (field.key === 'preisExpert') {
            const cDataRoot = appStore.clientData || {};
            const cdInner = cDataRoot.clientData || cDataRoot;
            
            const isProBono = cDataRoot.proBonoChecked || cdInner.proBonoChecked || cDataRoot.proBonoActive || cdInner.proBonoActive || false;
            const isForceSolo = cDataRoot.forceSoloTarif || cdInner.forceSoloTarif || false;
            const lizenzTypRaw = (cDataRoot.lizenzDetails?.typ || cdInner.lizenzDetails?.typ || '').toLowerCase();
            const isBasicChecked = cDataRoot.basicChecked || cdInner.basicChecked || false;
            const insiderSoloChecked = cDataRoot.insiderSoloChecked || cdInner.insiderSoloChecked || false;
            const insiderTeamStandardChecked = cDataRoot.insiderTeamleaderStandardChecked || cdInner.insiderTeamleaderStandardChecked || false;
            const insiderTeamPlusChecked = cDataRoot.insiderTeamleaderPlusChecked || cdInner.insiderTeamleaderPlusChecked || false;
            const manualMonthlyFee = Number(cDataRoot.monthlyFee ?? cdInner.monthlyFee) || 0;
            
            const gSettings = appStore.settings?.global || {};
            const limit = gSettings.Grobkundenaufschlag_ab || 42000;
            const aufschlag = (gSettings.Grobkundenaufschlag || 0.4) / 100;
            const basicPreis = gSettings.basicStandardPreis || 49;
            const soloPreis = gSettings.Insider_Solo || 74;
            const teamPreis = gSettings.Insider_Team || 167;

            const personalCount = calc.personalData?.length || 0;
            const monatsumsatzNetto = calc.monatsumsatzNetto || 0;
            const zeitraum = calc.zeitraum || 12;
            const prodLohnkostenfaktor = calc.prodLohnkostenfaktor || 0;

            let monat = 0;
            const isBasic = lizenzTypRaw.includes('basic') || isBasicChecked;
            const isInsiderSolo = lizenzTypRaw.includes('solo') || insiderSoloChecked;
            const isInsiderTeam = insiderTeamStandardChecked || insiderTeamPlusChecked || (lizenzTypRaw.includes('insider') && !lizenzTypRaw.includes('solo'));
            
            const hasAnyPackageFlag = isBasic || isInsiderSolo || isInsiderTeam;
            const hasProdMitarbeiter = prodLohnkostenfaktor > 0 || personalCount > 0;

            if (isProBono) {
                monat = 0;
            } else if (isForceSolo) {
                monat = soloPreis;
            } else if (hasAnyPackageFlag) {
                if (isBasic && !isInsiderTeam && !isInsiderSolo) {
                    monat = basicPreis;
                } else if (isInsiderSolo && !isInsiderTeam) {
                    monat = hasProdMitarbeiter ? teamPreis : soloPreis;
                } else {
                    const isPlusExplicit = insiderTeamPlusChecked;
                    monat = (monatsumsatzNetto >= limit || isPlusExplicit) ? (monatsumsatzNetto * aufschlag) : teamPreis;
                }
            } else {
                monat = manualMonthlyFee;
            }

            jahreswert = monat * zeitraum;
            isProBonoBadge = isProBono;
            proBonoUntilText = cDataRoot.proBonoEndDate || cdInner.proBonoEndDate || cDataRoot.proBonoUntil || cdInner.proBonoUntil || '';
        }

        const monatswert = calc.zeitraum > 0 ? jahreswert / calc.zeitraum : 0;
        const prozentwert = calc.monatsumsatzNetto > 0 ? (monatswert / calc.monatsumsatzNetto) * 100 : 0;

        return (
            <div key={field.key} className="grid grid-cols-12 gap-x-2 items-center border-b border-pe-border dark:border-slate-800 py-1.5 min-h-[44px] last:border-b-0 group/row relative text-left">
                <div className="col-span-4 text-sm font-bold text-pe-muted dark:text-slate-300 text-left flex items-center gap-2 flex-wrap">
                    {field.isLabelEditable ? (
                        <EditableField value={calc.bwaData[field.labelKey!] || ''} onChange={val => updateBwaData(field.labelKey!, val)} placeholder={field.placeholder} className="bg-pe-input dark:bg-[#0f1015] border-pe-border-strong dark:border-slate-700 h-10" />
                    ) : (
                        <span className={field.isOrange ? 'text-orange-700 dark:text-orange-400 font-bold text-left' : 'text-left'}>{field.label}</span>
                    )}
                    {field.key === 'betriebssteuern' && (
                        <label
                            className="inline-flex items-center gap-1.5 cursor-pointer select-none font-normal"
                            data-testid="gewerbesteuer-in-betriebssteuern-flag"
                            title="Gewerbesteuer in betrieblichen Steuern enthalten"
                        >
                            <input
                                type="checkbox"
                                checked={!!calc.bwaData?.gewerbesteuerInBetrieblichenSteuern}
                                onChange={(e) =>
                                    updateBwaData('gewerbesteuerInBetrieblichenSteuern', e.target.checked)
                                }
                                className="h-3.5 w-3.5 shrink-0 accent-[#DCB68A] cursor-pointer"
                            />
                            <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                                GewSt enthalten
                            </span>
                        </label>
                    )}
                    {BWA_PLAN_ZIEL_HINTS[field.key] && (
                        <span className={BWA_PLAN_ZIEL_HINT_CLASS}>{BWA_PLAN_ZIEL_HINTS[field.key]}</span>
                    )}
                    {isProBonoBadge && (
                        <span className="ml-2 text-[9px] bg-pink-100/70 dark:bg-pink-500/10 text-pink-700 dark:text-pink-400 border border-pink-300 dark:border-pink-500/20 px-2 py-0.5 rounded font-bold uppercase tracking-widest flex items-center gap-1 w-max shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
                            Pro Bono {proBonoUntilText ? `(bis ${proBonoUntilText})` : ''}
                        </span>
                    )}
                </div>
                
                <div className="col-start-6 col-span-2 text-left">
                    <EditableField 
                        value={jahreswert} 
                        onChange={(val: number) => updateBwaData(field.key, val)} 
                        type="currency" 
                        currency={currency} 
                        decimalScale={0}
                        decimalsLimit={0}
                        decimalPlaces={0}
                        precision={0}
                        readOnly={field.isCalculated} 
                        className={`h-10 ${field.isCalculated ? 'bg-transparent border-transparent text-pe-accent-text dark:text-[#E6C299]' : 'bg-pe-input dark:bg-[#0f1015] border-pe-border-strong dark:border-slate-700'}`} 
                    />
                </div>
                <div className="col-start-9 col-span-2 text-left">
                    <EditableField 
                        value={monatswert} 
                        readOnly 
                        type="currency" 
                        currency={currency} 
                        decimalScale={0}
                        decimalsLimit={0}
                        decimalPlaces={0}
                        precision={0}
                        className="bg-transparent border-transparent text-pe-muted dark:text-slate-400 h-10" 
                    />
                </div>
                <div className="col-start-11 col-span-2 text-left"><EditableField value={prozentwert} readOnly type="percentage" className="bg-transparent border-transparent text-stone-500 dark:text-slate-500 h-10" /></div>
                
                {field.isDevice && currentVisibleCount > 1 && (
                    <button 
                        onClick={() => handleDeleteGeraet(field.index)}
                        className="absolute -right-8 text-stone-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-500 transition-colors opacity-0 group-hover/row:opacity-100 p-2"
                        title="Gerät entfernen"
                    >
                        <IconTrash />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="bg-pe-bg dark:bg-[#08090b] min-h-screen p-4 sm:p-6 lg:p-8 relative font-sans text-left">
            {isPrognoseActive && (
                <div className="fixed bottom-[320px] right-8 z-[9999] animate-pulse">
                    <div className="bg-red-600 text-white px-4 py-2 rounded-full border-2 border-white shadow-[0_0_15px_rgba(220,38,38,0.8)] font-black text-xs uppercase tracking-tighter flex items-center gap-2 text-left">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        Prognose aktiv
                    </div>
                </div>
            )}

            {bwaScaleConfirm && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="bg-pe-card dark:bg-[#15171e] border border-pe-border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden p-6 md:p-8 flex flex-col gap-5">
                        <div className="absolute top-0 left-0 w-1 h-full bg-[#E6C299]" />
                        <div className="relative z-10 flex justify-between items-start gap-3">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299] mb-2">
                                    Kostenstruktur · Zeitraum
                                </p>
                                <h3 className="text-pe-text dark:text-white font-bold text-xl tracking-tight">
                                    {bwaScaleConfirm.mode === 'up'
                                        ? 'Auf 12 Monate hochrechnen?'
                                        : `Zurück auf ${bwaScaleConfirm.toMonths} Monate?`}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setBwaScaleConfirm(null)}
                                className="text-stone-500 dark:text-slate-500 hover:text-pe-text dark:hover:text-white p-2 border border-pe-border-strong dark:border-slate-700 bg-pe-tile-header dark:bg-[#08090b] rounded-lg transition-colors shadow-sm"
                                aria-label="Schließen"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="relative z-10 text-sm text-pe-muted dark:text-slate-400 leading-relaxed">
                            {bwaScaleConfirm.mode === 'up'
                                ? `Alle eingetragenen Werte werden proportional von ${bwaScaleConfirm.fromMonths} auf 12 Monate umgerechnet. Der Erfassungszeitraum wird auf 12 gesetzt. Du kannst die Hochrechnung jederzeit wieder lösen.`
                                : `Die Hochrechnung wird rückgängig gemacht. Alle Werte werden wieder auf ${bwaScaleConfirm.toMonths} Monate zurückgerechnet und der Zeitraum entsprechend gesetzt.`}
                        </p>
                        <div className="relative z-10 flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-pe-border dark:border-slate-800">
                            <button
                                type="button"
                                onClick={() => setBwaScaleConfirm(null)}
                                className="px-4 py-2.5 rounded-lg border border-pe-border-strong dark:border-slate-700 bg-transparent text-pe-muted dark:text-slate-300 text-xs font-bold uppercase tracking-widest hover:bg-pe-hover dark:hover:bg-[#1e293b] transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={executeBwaScaleConfirm}
                                className="px-4 py-2.5 rounded-lg bg-[#E6C299] text-[#08090b] text-xs font-black uppercase tracking-widest hover:bg-[#d4ac7d] transition-colors shadow-[0_0_15px_rgba(230,194,153,0.25)]"
                            >
                                {bwaScaleConfirm.mode === 'up' ? 'Hochrechnen' : 'Zurücksetzen'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BWA UPLOAD MODAL OVERLAY */}
            {yearNeedingUpload !== null && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="bg-pe-card dark:bg-[#15171e] border border-pe-border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl relative overflow-hidden p-8 flex flex-col gap-6">
                        <div className={`absolute top-0 left-0 w-1 h-full ${modalHasBwa ? 'bg-blue-500' : 'bg-red-500'}`}></div>
                        
                        <div className="relative z-10 flex justify-between items-start">
                            <div>
                                <h3 className="text-pe-text dark:text-white font-bold text-2xl mb-2 flex items-center gap-3">
                                    {modalHasBwa ? (
                                        <><FileCheck className="w-7 h-7 text-blue-700 dark:text-blue-400" /> BWA-Verwaltungs-Hub (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    ) : (
                                        <><AlertTriangle className="w-7 h-7 text-red-600 dark:text-red-500" /> BWA Fehlt! (<span className="font-mono-numbers">{yearNeedingUpload}</span>)</>
                                    )}
                                </h3>
                                <p className="text-slate-400 text-sm">
                                    {modalHasBwa 
                                        ? `Hier siehst du alle hochgeladenen Dokumente. Du kannst falsche PDFs löschen oder über das Feld unten weitere anhängen.`
                                        : `Lade hier deine aktuelle BWA hoch. Für den Jahresabschluss benötigen wir zwingend die finale Dezember-BWA des Vorjahres. Du kannst hier auch mehrere PDFs auf einmal markieren.`}
                                </p>
                            </div>
                            <button onClick={() => setYearNeedingUpload(null)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text dark:hover:text-white p-2 border border-pe-border-strong dark:border-slate-700 bg-pe-tile-header dark:bg-[#08090b] rounded-lg transition-colors shadow-sm">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {modalHasBwa && modalBwaList.length > 0 && (
                            <div className="relative z-10 bg-pe-tile-header dark:bg-[#08090b] border border-pe-border dark:border-slate-800 rounded-xl p-4 flex flex-col gap-2">
                                <h4 className="text-[10px] text-stone-500 dark:text-slate-500 uppercase tracking-widest font-bold mb-2">Hochgeladene Dateien:</h4>
                                {modalBwaList.map((url: string, idx: number) => (
                                    <div key={idx} className="flex items-center justify-between bg-pe-card dark:bg-[#15171e] border border-pe-border-strong dark:border-slate-700 p-3 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-blue-700 dark:text-blue-400" />
                                            <span className="text-sm font-bold font-mono-numbers text-pe-muted dark:text-slate-300">BWA_Dokument_Teil_{idx + 1}.pdf</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => handleOpenBwa(url)}
                                                className="text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 p-2 bg-blue-100/70 dark:bg-blue-900/20 hover:bg-blue-200/70 dark:hover:bg-blue-900/40 border border-blue-300 dark:border-blue-500/20 rounded transition-colors"
                                                title="BWA Anzeigen"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM0,160H40V56H216V200ZM176,88a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,88Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,128Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,168Z"></path>
                                                </svg>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteSingleBwa(url, yearNeedingUpload)}
                                                disabled={isDeletingUrl === url || isUploading}
                                                className="text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 p-2 bg-red-100/70 dark:bg-red-900/20 hover:bg-red-200/70 dark:hover:bg-red-900/40 border border-red-300 dark:border-red-500/20 rounded transition-colors"
                                                title="Datei unwiderruflich löschen"
                                            >
                                                {isDeletingUrl === url ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!modalHasBwa && (
                            <div className="relative z-10 bg-red-100/80 dark:bg-red-950/20 border border-red-300 dark:border-red-900/50 rounded-xl p-5 shadow-inner">
                                <ul className="text-pe-muted dark:text-slate-300 text-sm space-y-2 list-disc list-inside pl-2 marker:text-red-600 dark:marker:text-red-500/70">
                                    <li>Lade für den Jahresabschluss die <strong>Dezember-BWA</strong> des Vorjahres hoch, oder bei Bedarf deine <strong>aktuelle unterjährige BWA</strong>.</li>
                                    <li>Die BWA muss zwingend den <strong>Wertenachweis</strong> (Kontennachweis) enthalten <strong>(keine Summen- und Saldenliste!)</strong>. Schau dir dazu bitte die Muster-BWA an.</li>
                                    <li><strong>Keine Fotos!</strong> Wir benötigen das originale PDF deines Steuerberaters.</li>
                                </ul>
                            </div>
                        )}

                        <div className="relative z-10 w-full h-32 group">
                            <input 
                                id="bwa-upload-input"
                                type="file" 
                                accept=".pdf" 
                                multiple
                                onChange={(e) => handleLateBwaUpload(e, yearNeedingUpload)}
                                disabled={isUploading}
                                className="hidden" 
                            />
                            <label 
                                htmlFor="bwa-upload-input"
                                className={`w-full h-full border-2 border-dashed rounded-2xl transition-all flex flex-col items-center justify-center cursor-pointer 
                                    ${modalHasBwa ? 'border-blue-300 dark:border-blue-900/50 bg-blue-100/50 dark:bg-blue-900/10 hover:border-blue-500' : 'border-red-300 dark:border-red-900/50 bg-red-100/60 dark:bg-red-900/20 hover:border-red-500'} 
                                    ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                                {isUploading ? (
                                    <Loader2 className={`w-8 h-8 animate-spin mb-2 ${modalHasBwa ? 'text-blue-700 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`} />
                                ) : (
                                    <Upload className={`w-8 h-8 mb-2 transition-colors group-hover:-translate-y-1 ${modalHasBwa ? 'text-blue-300 dark:text-blue-900/50 group-hover:text-blue-700 dark:group-hover:text-blue-400' : 'text-red-300 dark:text-red-900/50 group-hover:text-red-600 dark:group-hover:text-red-400'}`} />
                                )}
                                <span className={`text-sm font-bold transition-colors ${modalHasBwa ? 'text-blue-700/80 dark:text-blue-400/70 group-hover:text-blue-800 dark:group-hover:text-blue-400' : 'text-red-600/80 dark:text-red-400/70 group-hover:text-red-700 dark:group-hover:text-red-400'}`}>
                                    {isUploading ? 'Dokumente werden hochgeladen...' : 'Neues PDF hier auswählen oder reinziehen'}
                                </span>
                            </label>
                        </div>

                        <div className="relative z-10 flex justify-between items-center pt-2 border-t border-pe-border dark:border-slate-800">
                            <button 
                                type="button"
                                onClick={() => setYearNeedingUpload(null)} 
                                className="text-stone-500 dark:text-slate-500 hover:text-pe-text dark:hover:text-white text-xs font-bold uppercase tracking-widest transition-colors underline"
                            >
                                {modalHasBwa ? 'Schließen & Zurück' : 'Später hochladen & Zurück'}
                            </button>

                            <button 
                                type="button"
                                onClick={() => setShowBwaModal(true)} 
                                className="inline-flex items-center gap-2 text-pe-accent-text dark:text-[#E6C299] hover:text-pe-text dark:hover:text-white text-xs font-bold uppercase tracking-widest transition-colors"
                            >
                                <Download className="w-4 h-4" /> Muster-BWA ansehen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBwaModal && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-900/50 dark:bg-black/90 backdrop-blur-sm p-4 md:p-8 animate-fadeIn">
                    <div className="bg-pe-card dark:bg-[#15171e] border border-pe-border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col relative overflow-hidden">
                        <div className="relative z-10 p-4 border-b border-pe-border dark:border-slate-800 flex justify-between items-center bg-pe-tile-header dark:bg-[#08090b]">
                            <h3 className="text-pe-text dark:text-white font-bold flex items-center gap-2 uppercase tracking-widest text-sm">
                                <FileText className="w-5 h-5 text-pe-accent-text dark:text-[#E6C299]" /> Muster-BWA (Beispiel)
                            </h3>
                            <button onClick={() => setShowBwaModal(false)} className="text-stone-500 dark:text-slate-500 hover:text-pe-text dark:hover:text-white p-2 border border-pe-border dark:border-slate-800 bg-pe-card dark:bg-[#15171e] rounded-lg transition-colors shadow-sm">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="relative z-10 flex-1 bg-pe-tile-header dark:bg-[#08090b] p-2">
                            <iframe 
                                src="/assets/muster-bwa.pdf" 
                                className="w-full h-full rounded-xl bg-white"
                                title="Muster BWA"
                            />
                        </div>
                        <div className="relative z-10 p-4 border-t border-pe-border dark:border-slate-800 bg-pe-tile-header dark:bg-[#08090b] flex justify-between items-center">
                            <a 
                                href="/assets/muster-bwa.pdf" 
                                download="Muster_BWA_PreisExpert.pdf"
                                className="text-pe-muted dark:text-slate-400 hover:text-pe-accent-text dark:hover:text-[#E6C299] text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
                            >
                                <Download className="w-4 h-4" /> PDF Herunterladen
                            </a>
                            <button onClick={() => setShowBwaModal(false)} className="bg-pe-card dark:bg-[#15171e] border border-pe-border-strong dark:border-slate-700 hover:bg-pe-hover dark:hover:bg-slate-800 text-pe-text dark:text-white px-6 py-2.5 rounded-lg text-xs tracking-widest uppercase font-bold transition-colors shadow-sm">
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* / BWA UPLOAD MODAL OVERLAY */}

            <div className="w-full max-w-[1100px] mx-auto text-left">
                <UI.Section className="text-left bg-transparent border-transparent shadow-none p-0"> 
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 text-left gap-4 border-b border-pe-border dark:border-slate-800 pb-6">
                        <div className="flex flex-col items-start gap-2">
                            <h1 className="text-3xl font-black text-pe-text dark:text-white tracking-widest uppercase mb-1">Kostenstruktur</h1>
                            <p className="text-slate-400 text-sm">BWA-Daten & Unternehmensausgaben</p>
                            {isPrognoseActive && <span className="mt-2 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-400 border border-red-300 dark:border-red-500/50 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-left shadow-sm">Achtung: Werte inkl. Mitarbeiter-Prognose</span>}
                        </div>
                        
                        {/* 💉 DER GOLDENE BWA ÖFFNEN BUTTON SOWIE DER VERWALTUNGS BUTTON */}
                        <div className="flex flex-wrap items-center justify-end gap-3 w-full md:w-auto">
                            <button
                                onClick={() => setYearNeedingUpload(Number(currentYear))}
                                className={`px-4 py-2 rounded-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-lg border hover:-translate-y-0.5 whitespace-nowrap animate-in fade-in ${
                                    bwaList.length > 0 
                                    ? 'bg-pe-card dark:bg-[#15171e] text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#1e293b] border-pe-border-strong dark:border-slate-700' 
                                    : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800 border-red-300 dark:border-red-500/50'
                                }`}
                                title="BWA Verwaltung öffnen"
                            >
                                {bwaList.length > 0 ? <FileCheck className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                {bwaList.length > 0 ? 'BWA VERWALTEN' : 'BWA HOCHLADEN'}
                            </button>

                            {bwaList.length > 0 && bwaList.map((url, idx) => (
                                <button 
                                    key={url}
                                    onClick={() => handleOpenBwa(url)} 
                                    className="bg-[#E6C299] text-[#08090b] px-4 py-2 rounded-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-[#d4ac7d] transition-all shadow-[0_0_15px_rgba(230,194,153,0.3)] border border-[#E6C299] hover:-translate-y-0.5 whitespace-nowrap animate-in fade-in"
                                    title={`BWA Datei ${idx + 1} öffnen`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                        <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM0,160H40V56H216V200ZM176,88a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,88Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,128Zm0,40a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,168Z"></path>
                                    </svg>
                                    {bwaList.length > 1 ? `BWA Teil ${idx + 1} ÖFFNEN` : `BWA ${currentYear} ÖFFNEN`}
                                </button>
                            ))}
                        </div>
                    </div>

                    {calc.activeLand === 'CH' &&
                        Number(calc.mwstSatz) === 0 &&
                        Math.max(
                            Number(calc.gesamtUmsatz) || 0,
                            (Number(calc.sollumsatz) || 0) * (1 + (Number(calc.liquiditaet) || 0) / 100) * 12
                        ) >= (Number(calc.chVatStatus?.vatThreshold) || 100000) && (
                            <div className="mb-6 bg-[#15171e] border border-[#dcb68a]/50 rounded-xl p-4 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest bg-[#dcb68a]/20 text-[#dcb68a] border border-[#dcb68a]/50 shrink-0 mt-0.5">
                                        CH · ART. 10 MWSTG
                                    </span>
                                    <div>
                                        <p className="text-xs sm:text-sm font-bold text-white">
                                            Zielumsatz erreicht 100&apos;000 CHF: Gesetzliche MWST-Pflicht entsteht. Jetzt auf 8,1 % MWST umstellen?
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            Aktuell wird mit 0,0 % (Befreiung unter 100&apos;000 CHF) kalkuliert. Ab 100&apos;000 CHF Jahresumsatz gilt der Schweizer Normalsatz von 8,1 %.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (typeof appStore.captureState === 'function') appStore.captureState();
                                        const prevOverrides = appStore.settings?.countryOverrides || {};
                                        const prevCh = prevOverrides.CH || {};
                                        if (typeof appStore.updateSettings === 'function') {
                                            appStore.updateSettings('countryOverrides', {
                                                ...prevOverrides,
                                                CH: {
                                                    ...prevCh,
                                                    isVatExempt: false,
                                                    voluntaryOption: true,
                                                },
                                            });
                                        }
                                        if (typeof setClientData === 'function') {
                                            setClientData((prev: any) => ({
                                                ...(prev || {}),
                                                umsatzsteuerbefreit: false,
                                                isVatExempt: false,
                                            }));
                                        }
                                    }}
                                    className="shrink-0 bg-[#dcb68a] hover:bg-[#c9a375] text-[#08090b] font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-lg transition-all shadow-md"
                                >
                                    Auf 8,1 % MWST umstellen
                                </button>
                            </div>
                        )}

                    <div className="bg-pe-card dark:bg-[#15171e] rounded-2xl border border-pe-border dark:border-slate-800 shadow-2xl p-5 md:p-6 mb-8">
                        <div className="grid grid-cols-12 gap-x-4 items-center mb-3 text-pe-accent-text dark:text-[#E6C299] font-black text-[10px] uppercase tracking-widest text-left border-b border-pe-border dark:border-slate-800 pb-2">
                            <div className="col-span-4 text-left">Kostenart</div>
                            <div className="col-start-6 col-span-2 text-center">Wert für Zeitraum</div>
                            <div className="col-start-9 col-span-2 text-center">pro Monat</div>
                            <div className="col-start-11 col-span-2 text-center">%</div>
                        </div>

                        <div className="grid grid-cols-12 gap-x-4 items-center py-3 text-left border-b border-pe-border dark:border-slate-800">
                            <div className="col-span-4 text-sm font-black uppercase tracking-widest text-pe-text dark:text-white text-left">Jahresumsatz netto (BWA)</div>
                            <div className="col-start-6 col-span-2 text-left">
                                <EditableField 
                                    value={calc.bwaData.umsatz || 0} 
                                    onChange={(val:number) => updateBwaData('umsatz', val)} 
                                    type="currency" 
                                    currency={currency} 
                                    decimalScale={0}
                                    decimalsLimit={0}
                                    decimalPlaces={0}
                                    precision={0}
                                    className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers text-sm font-bold text-pe-text dark:text-white shadow-inner h-10" 
                                />
                            </div>
                            <div className="col-start-9 col-span-4 flex items-center justify-end gap-2 text-left">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500">Zeitraum</span>
                                <UI.Select
                                    value={calc.bwaData.zeitraum || 12}
                                    onChange={(e) => handleZeitraumChange(parseInt(e.target.value, 10))}
                                    disabled={isBwaScaledTo12}
                                    title={
                                        isBwaScaledTo12
                                            ? `Gesperrt: zuerst Hochrechnung lösen (Zurück · ${bwaScaleSourceMonths} Mon.)`
                                            : undefined
                                    }
                                    className={`bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 text-sm font-bold text-pe-text dark:text-white w-20 shadow-inner h-10 ${
                                        isBwaScaledTo12 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (<option key={m} value={m}>{m}</option>))}
                                </UI.Select>
                                <button
                                    type="button"
                                    onClick={handleToggleBwaScaleTo12}
                                    disabled={!canToggleBwaScale}
                                    title={
                                        isBwaScaledTo12
                                            ? `Hochrechnung lösen (${bwaScaleSourceMonths} Monate)`
                                            : canToggleBwaScale
                                              ? `Von ${bwaZeitraum} auf 12 Monate hochrechnen`
                                              : 'Zeitraum 1–11 wählen'
                                    }
                                    className="h-10 px-3 rounded-lg border border-pe-border-strong dark:border-slate-700 bg-pe-tile-header dark:bg-[#08090b] text-[10px] font-bold uppercase tracking-widest text-pe-muted dark:text-slate-300 hover:text-pe-accent-text dark:hover:text-[#E6C299] hover:border-[#E6C299]/40 transition-colors disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:text-pe-muted dark:disabled:hover:text-slate-300 disabled:hover:border-pe-border-strong dark:disabled:hover:border-slate-700"
                                >
                                    {isBwaScaledTo12 ? `Zurück · ${bwaScaleSourceMonths} Mon.` : '× 12 Monate'}
                                </button>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-12 gap-x-4 items-center py-3 text-left border-b border-pe-border dark:border-slate-800">
                            <div className="col-span-4 text-sm font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] text-left">Gesamtumsatz inkl. Prognose</div>
                            <div className="col-start-6 col-span-2 text-left">
                                <EditableField 
                                    value={calc.gesamtUmsatz} 
                                    readOnly 
                                    type="currency" 
                                    currency={currency} 
                                    decimalScale={0}
                                    decimalsLimit={0}
                                    decimalPlaces={0}
                                    precision={0}
                                    className="bg-transparent border-transparent text-pe-accent-text dark:text-[#E6C299] font-mono-numbers text-sm font-bold h-10" 
                                />
                            </div>
                            <div className="col-start-9 col-span-4 flex flex-col sm:flex-row justify-end items-end gap-2">
                                <div className="flex items-center gap-2 bg-pe-tile-header dark:bg-[#08090b] px-3 py-1.5 rounded border border-pe-border dark:border-slate-800">
                                    <span className="text-[9px] uppercase font-bold tracking-widest text-stone-500 dark:text-slate-500">Brutto:</span>
                                    <div className="w-24">
                                        <EditableField 
                                            value={calc.monatsumsatzBrutto} 
                                            readOnly 
                                            type="currency" 
                                            currency={currency} 
                                            decimalScale={0}
                                            decimalsLimit={0}
                                            decimalPlaces={0}
                                            precision={0}
                                            className="bg-transparent border-transparent text-xs font-mono-numbers text-pe-muted dark:text-slate-300 text-right w-full p-0" 
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-pe-tile-header dark:bg-[#08090b] px-3 py-1.5 rounded border border-emerald-300 dark:border-emerald-900/30">
                                    <span className="text-[9px] uppercase font-black tracking-widest text-emerald-700 dark:text-emerald-500">Netto:</span>
                                    <div className="w-24">
                                        <EditableField 
                                            value={calc.monatsumsatzNetto} 
                                            readOnly 
                                            type="currency" 
                                            currency={currency} 
                                            decimalScale={0}
                                            decimalsLimit={0}
                                            decimalPlaces={0}
                                            precision={0}
                                            className="bg-transparent border-transparent text-xs font-mono-numbers font-black text-emerald-700 dark:text-emerald-400 text-right w-full p-0" 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`grid grid-cols-12 gap-x-4 items-center border border-pe-border dark:border-slate-800 rounded-lg px-4 py-2 mt-3 min-h-[44px] text-left ${isPrognoseActive ? 'bg-orange-100/60 dark:bg-orange-950/10' : 'bg-emerald-100/60 dark:bg-emerald-950/10'}`}>
                            <div className="col-span-4 text-sm font-bold uppercase tracking-widest flex items-center gap-2 text-left">
                                <span className={isPrognoseActive ? 'text-orange-700 dark:text-orange-400' : 'text-emerald-700 dark:text-emerald-400'}>Materialeinsatz</span>
                                <span className={BWA_PLAN_ZIEL_HINT_CLASS}>8-10% Planziel</span>
                                {isPrognoseActive && <span className="text-[8px] uppercase bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/30 text-orange-700 dark:text-orange-400 font-black tracking-widest">Prognose berechnet</span>}
                            </div>
                            <div className="col-start-6 col-span-2 text-left">
                                <EditableField 
                                    value={calc.bwaData.material || 0} 
                                    onChange={(val:number) => updateBwaData('material', val)} 
                                    type="currency" 
                                    currency={currency} 
                                    decimalScale={0}
                                    decimalsLimit={0}
                                    decimalPlaces={0}
                                    precision={0}
                                    className="bg-pe-input dark:bg-[#08090b] border-pe-border-strong dark:border-slate-700 font-mono-numbers text-sm text-pe-text dark:text-white shadow-inner h-10" 
                                />
                            </div>
                            <div className="col-start-9 col-span-2 text-left">
                                <EditableField 
                                    value={calc.materialkostenMonat} 
                                    readOnly 
                                    type="currency" 
                                    currency={currency} 
                                    decimalScale={0}
                                    decimalsLimit={0}
                                    decimalPlaces={0}
                                    precision={0}
                                    className="bg-transparent border-transparent font-mono-numbers text-sm text-pe-muted dark:text-slate-400 h-10" 
                                />
                            </div>
                            <div className="col-start-11 col-span-2 text-left"><EditableField value={calc.baseMaterialProzent} readOnly type="percentage" className={`bg-transparent border-transparent font-mono-numbers text-sm h-10 ${isPrognoseActive ? 'text-orange-700 dark:text-orange-400 font-black' : 'text-stone-500 dark:text-slate-500'}`} /></div>
                        </div>

                        <UI.CollapsibleSection title="BWA Kostenarten" initialOpen={true}>
                            <div className="space-y-6 text-left mt-4"> 
                                {bwaFieldGroups.map(group => (
                                    <div key={group.key} className="text-left bg-pe-elevated dark:bg-[#08090b] rounded-xl border border-pe-border dark:border-slate-800 overflow-hidden shadow-inner">
                                        <div className={`flex justify-between items-center px-5 py-3 ${group.color} text-left border-b border-pe-border dark:border-slate-800`}>
                                            <h4 className="text-[10px] font-black uppercase tracking-widest text-pe-text dark:text-white">{group.title}</h4>
                                            {group.key === 'geraete' && (
                                                <span className="text-pe-accent-text dark:text-[#E6C299] bg-white/60 dark:bg-black/40 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest border border-[#E6C299]/20 shadow-sm">
                                                    Gesamt: {formatNumberOnly(calc.geraetekostenGesamt)} {currency}
                                                </span>
                                            )}
                                        </div>
                                        <div className="px-5 py-2 text-left"> 
                                            {group.fields.map(field => (
                                                <React.Fragment key={field.key || field.labelKey || field.label}>
                                                    {renderRow(field)}
                                                </React.Fragment>
                                            ))}
                                            
                                            {group.key === 'geraete' && (
                                                <div className="mt-3 pt-3 border-t border-pe-border dark:border-slate-800 flex flex-col items-start gap-2 text-left pb-2">
                                                    {currentVisibleCount < 10 ? (
                                                        <button 
                                                            onClick={() => updateBwaData('visibleGeraeteCount', currentVisibleCount + 1)}
                                                            className="h-8 flex items-center justify-center rounded-lg px-4 bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1e293b] text-pe-muted dark:text-slate-300 transition-colors border border-pe-border-strong dark:border-slate-700 hover:border-stone-400 dark:hover:border-slate-500 text-[10px] font-bold uppercase tracking-widest gap-2"
                                                            title="Weiteres Gerät hinzufügen"
                                                        >
                                                            <PlusIcon size={12} /> Neues Gerät
                                                        </button>
                                                    ) : (
                                                        <div className="bg-orange-100/80 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-500/30 text-orange-900 dark:text-orange-400 text-[10px] uppercase tracking-widest px-4 py-2 rounded-lg w-full text-center font-bold">
                                                            Limit erreicht: Es können keine weiteren Geräte hinzugefügt werden.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </UI.CollapsibleSection>
                    </div>

                    <div className="mt-8 space-y-8 text-left">
                        <CostTool calc={calc} />
                        <Kurzauswertung calc={calc} />

                        <UI.CollapsibleSection title="Mitarbeiter Prognosen" initialOpen={false}>
                            <div className="flex flex-col gap-6 text-left mt-4">
                                {isPrognoseActive && (
                                    <div className="flex justify-center -mb-2 text-left">
                                        <span className="bg-[#E6C299] text-[#08090b] px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-[0_0_15px_rgba(230,194,153,0.3)] border border-[#E6C299] text-left">
                                            + {formatNumberOnly(activePrognoseUmsatz)} {currency} Prognose-Umsatz AKTIVIERT
                                        </span>
                                    </div>
                                )}
                                <MitarbeiterUmsatzprognose calc={calc} /> 
                                <PrognoseAktivierung calc={calc} />
                            </div>
                        </UI.CollapsibleSection>

                        <UI.CollapsibleSection title="BWA-Soll-Umsatz Abgleich (Faktor-Check)" initialOpen={false}>
                            <div className="flex flex-col gap-6 text-left mt-4">
                                <BwaUmsatzAbgleich calc={calc} />
                            </div>
                        </UI.CollapsibleSection>
                    </div>
                </UI.Section>
            </div>
        </div>
    );
};

export default KostenstrukturPage;